import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useOp } from '../../api.ts';
import { Link, useRouter } from '../../router.tsx';
import { runWrite, useChanges } from '../../ui/changes.ts';
import { step, useKeys } from '../../ui/keys.ts';
import { Empty, ErrorBox, Kbd, Loading, formatDate } from '../../ui/kit.tsx';
import { useToast } from '../../ui/toasts.tsx';
import { PAGE_LABELS, WIDGET_LABELS, splitList, type PageType, type Region, type Widget, type WidgetLink, type WidgetOptions, type WidgetType } from './types.ts';
import './content.css';

/**
 * Widgets (docs/campaigns-and-widgets.md): written once, placed by rules (page type, region, targeting, dates) on
 * any of the organization's sites, filled from live data where possible. Only approved widgets show on the sites.
 */
export function WidgetList() {
  const changes = useChanges();
  const { navigate } = useRouter();
  const list = useOp<{ widgets: Widget[] }>('admin.widgets.list', {}, changes);
  const [cursor, setCursor] = useState(0);
  const rows = list.data?.widgets ?? [];
  useKeys({
    j: () => setCursor((c) => step(c, 1, rows.length)),
    k: () => setCursor((c) => step(c, -1, rows.length)),
    enter: () => rows[cursor] && navigate(`/widgets/${rows[cursor]!.ref}`),
    n: () => navigate('/widgets/new'),
  });
  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Widgets</h1>
          <p className="muted">
            Hero carousels, featured stores, top codes, banners and text blocks. Write a widget once and place it by rule: every homepage, all Fashion category
            pages, the Black Friday page. Sold positions name the brand and show a Sponsored label.
          </p>
        </div>
        <Link className="btn btn-primary" to="/widgets/new">
          New widget <Kbd>n</Kbd>
        </Link>
      </header>
      {list.error ? <ErrorBox error={list.error} onRetry={list.reload} /> : null}
      {!list.data && !list.error ? <Loading /> : null}
      {list.data && rows.length === 0 ? <Empty>No widgets yet.</Empty> : null}
      {rows.length ? (
        <table className="table" aria-label="Widgets">
          <thead>
            <tr>
              <th scope="col">Widget</th>
              <th scope="col">Type</th>
              <th scope="col">Status</th>
              <th scope="col">Shows on</th>
              <th scope="col">Sponsored</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const live = row.placements.filter((p) => p.state !== 'ended');
              const sponsors = [...new Set(row.placements.map((p) => p.sponsoredBy).filter(Boolean))];
              return (
                <tr key={row.ref} className={`clickable ${index === cursor ? 'cursor' : ''}`} onClick={() => navigate(`/widgets/${row.ref}`)} onMouseEnter={() => setCursor(index)}>
                  <td>
                    <strong>{row.name}</strong>
                  </td>
                  <td>{WIDGET_LABELS[row.type]}</td>
                  <td>
                    <span className={`badge ${row.status === 'approved' ? 'badge-ok' : ''}`}>{row.status}</span>
                  </td>
                  <td className="small">
                    {live.length
                      ? [...new Set(live.map((p) => `${p.siteName}: ${PAGE_LABELS[p.pageType].toLowerCase()} (${p.region})`))].join(' · ')
                      : <span className="muted">not placed</span>}
                  </td>
                  <td className="small">{sponsors.length ? sponsors.join(', ') : <span className="muted">house</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </section>
  );
}

type Config = Record<string, unknown>;

const DEFAULT_CONFIG: Record<WidgetType, Config> = {
  hero_carousel: { slides: [{ headline: '', text: '', cta: '', link: { kind: 'merchant', slug: '' } }] },
  featured_merchants: { title: '', source: 'page', pinned: [], excluded: [], limit: 8 },
  top_codes: { title: '', source: 'page', pinned: [], merchants: [], excludedMerchants: [], limit: 6 },
  banner: { headline: '', text: '', cta: '', link: { kind: 'campaign', slug: '' } },
  text: { heading: '', paragraphs: [], faq: [] },
};

/** Drops empty optional strings so the API's checks see only what was filled in. */
function cleaned(config: Config): Config {
  const out: Config = {};
  for (const [key, value] of Object.entries(config)) {
    if (value === '' || value === undefined) continue;
    if (key === 'slides' && Array.isArray(value)) out[key] = value.map((slide) => cleaned(slide as Config));
    else if (key === 'link' && value && typeof value === 'object') out[key] = cleaned(value as Config);
    else out[key] = value;
  }
  return out;
}

function LinkField({ value, onChange }: { value: WidgetLink; onChange: (link: WidgetLink) => void }) {
  const field = value.kind === 'url' ? 'url' : value.kind === 'coupon' ? 'ref' : 'slug';
  return (
    <span className="inline-field">
      <select value={value.kind} onChange={(e) => onChange({ kind: e.target.value as WidgetLink['kind'] })}>
        <option value="merchant">Store page</option>
        <option value="category">Category page</option>
        <option value="campaign">Campaign page</option>
        <option value="coupon">Coupon (clickout)</option>
        <option value="url">External https link</option>
      </select>
      <input
        required
        value={(value[field] as string | undefined) ?? ''}
        placeholder={field === 'url' ? 'https://…' : field === 'ref' ? 'cpn_…' : 'slug, e.g. nike'}
        onChange={(e) => onChange({ kind: value.kind, [field]: e.target.value })}
      />
    </span>
  );
}

function ConfigForm({ type, config, onChange }: { type: WidgetType; config: Config; onChange: (config: Config) => void }) {
  const set = (key: string, value: unknown) => onChange({ ...config, [key]: value });
  const text = (key: string) => (config[key] as string | undefined) ?? '';
  const list = (key: string) => ((config[key] as string[] | undefined) ?? []).join(', ');
  if (type === 'hero_carousel') {
    const slides = (config['slides'] as Config[] | undefined) ?? [];
    const setSlide = (index: number, patch: Config) => set('slides', slides.map((s, i) => (i === index ? { ...s, ...patch } : s)));
    return (
      <>
        {slides.map((slide, index) => (
          <div className="slide-card" key={index}>
            <div className="form-grid">
              <label>
                Headline
                <input required maxLength={90} value={(slide['headline'] as string) ?? ''} onChange={(e) => setSlide(index, { headline: e.target.value })} />
              </label>
              <label>
                Button
                <input maxLength={40} value={(slide['cta'] as string) ?? ''} onChange={(e) => setSlide(index, { cta: e.target.value })} />
              </label>
              <label className="c-wide">
                Text
                <input maxLength={200} value={(slide['text'] as string) ?? ''} onChange={(e) => setSlide(index, { text: e.target.value })} />
              </label>
              <label className="c-wide">
                Link (a store slide takes the store's colour)
                <LinkField value={slide['link'] as WidgetLink} onChange={(link) => setSlide(index, { link })} />
              </label>
            </div>
            <button type="button" className="btn btn-small" disabled={slides.length <= 1} onClick={() => set('slides', slides.filter((_, i) => i !== index))}>
              Remove slide
            </button>
          </div>
        ))}
        <button type="button" className="btn btn-small" disabled={slides.length >= 6} onClick={() => set('slides', [...slides, { headline: '', link: { kind: 'merchant', slug: '' } }])}>
          Add slide
        </button>
      </>
    );
  }
  if (type === 'banner') {
    return (
      <div className="form-grid">
        <label>
          Headline
          <input required maxLength={90} value={text('headline')} onChange={(e) => set('headline', e.target.value)} />
        </label>
        <label>
          Button
          <input required maxLength={40} value={text('cta')} onChange={(e) => set('cta', e.target.value)} />
        </label>
        <label className="c-wide">
          Text
          <input maxLength={240} value={text('text')} onChange={(e) => set('text', e.target.value)} />
        </label>
        <label className="c-wide">
          Link
          <LinkField value={config['link'] as WidgetLink} onChange={(link) => set('link', link)} />
        </label>
      </div>
    );
  }
  if (type === 'text') {
    const faq = (config['faq'] as { question: string; answer: string }[] | undefined) ?? [];
    return (
      <div className="form-grid">
        <label className="c-wide">
          Heading
          <input maxLength={120} value={text('heading')} onChange={(e) => set('heading', e.target.value)} />
        </label>
        <label className="c-wide">
          Paragraphs (blank line between paragraphs)
          <textarea rows={5} value={((config['paragraphs'] as string[] | undefined) ?? []).join('\n\n')} onChange={(e) => set('paragraphs', e.target.value.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean))} />
        </label>
        {faq.map((item, index) => (
          <label className="c-wide" key={index}>
            Question {index + 1}
            <input value={item.question} onChange={(e) => set('faq', faq.map((f, i) => (i === index ? { ...f, question: e.target.value } : f)))} />
            <textarea rows={2} value={item.answer} onChange={(e) => set('faq', faq.map((f, i) => (i === index ? { ...f, answer: e.target.value } : f)))} />
          </label>
        ))}
        <button type="button" className="btn btn-small" onClick={() => set('faq', [...faq, { question: '', answer: '' }])}>
          Add question
        </button>
      </div>
    );
  }
  // featured_merchants and top_codes: filled from live data, with pins.
  return (
    <div className="form-grid">
      <label className="c-wide">
        Title (empty = default)
        <input maxLength={90} value={text('title')} onChange={(e) => set('title', e.target.value)} />
      </label>
      <label>
        Fill from
        <select value={text('source') || 'page'} onChange={(e) => set('source', e.target.value)}>
          <option value="page">The page it is on (its category or campaign)</option>
          <option value="site">The whole site</option>
          <option value="category">A category</option>
          <option value="campaign">A campaign</option>
        </select>
      </label>
      <label>
        How many
        <input type="number" min={1} max={type === 'top_codes' ? 12 : 16} value={Number(config['limit'] ?? 6)} onChange={(e) => set('limit', Number(e.target.value))} />
      </label>
      {config['source'] === 'category' ? (
        <label>
          Category slug
          <input value={text('categorySlug')} onChange={(e) => set('categorySlug', e.target.value)} />
        </label>
      ) : null}
      {config['source'] === 'campaign' ? (
        <label>
          Campaign slug
          <input value={text('campaignSlug')} onChange={(e) => set('campaignSlug', e.target.value)} />
        </label>
      ) : null}
      {type === 'featured_merchants' ? (
        <>
          <label className="c-wide">
            Pinned stores first (slugs, comma-separated)
            <input value={list('pinned')} onChange={(e) => set('pinned', splitList(e.target.value))} />
          </label>
          <label className="c-wide">
            Never show (slugs)
            <input value={list('excluded')} onChange={(e) => set('excluded', splitList(e.target.value))} />
          </label>
        </>
      ) : (
        <>
          <label className="c-wide">
            Only these stores (slugs; empty = any)
            <input value={list('merchants')} onChange={(e) => set('merchants', splitList(e.target.value))} />
          </label>
          <label className="c-wide">
            Pinned coupons first (refs; skipped when no longer live)
            <input value={list('pinned')} onChange={(e) => set('pinned', splitList(e.target.value))} />
          </label>
          <label className="c-wide">
            Never from these stores (slugs)
            <input value={list('excludedMerchants')} onChange={(e) => set('excludedMerchants', splitList(e.target.value))} />
          </label>
        </>
      )}
    </div>
  );
}

interface SiteRow {
  ref: string;
  name: string;
}

function PlacementForm({ widget, options, sites }: { widget: Widget; options: WidgetOptions; sites: SiteRow[] }) {
  const toast = useToast();
  const allowedPages = options.pageTypes.filter((p) => p !== 'merchant' || options.merchantPageTypes.includes(widget.type));
  const [siteRefs, setSiteRefs] = useState<string[]>(sites.length === 1 ? [sites[0]!.ref] : []);
  const [pageType, setPageType] = useState<PageType>(allowedPages[0] ?? 'home');
  const [region, setRegion] = useState<Region>(options.regionsByPage[pageType]?.[0] ?? 'top');
  const [targets, setTargets] = useState({ categories: '', merchants: '', campaigns: '' });
  const [dates, setDates] = useState({ startsAt: '', endsAt: '' });
  const [sponsoredBy, setSponsoredBy] = useState('');
  useEffect(() => setRegion(options.regionsByPage[pageType]?.[0] ?? 'top'), [pageType, options]);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const target = Object.fromEntries(Object.entries(targets).map(([key, value]) => [key, splitList(value)]).filter(([, value]) => (value as string[]).length));
    const done = await runWrite(
      toast,
      'admin.widgets.place',
      { widgetRef: widget.ref, siteRefs, pageType, region, targets: target, startsAt: dates.startsAt || null, endsAt: dates.endsAt || null, sponsoredBy: sponsoredBy.trim() || null },
      'Placed',
    );
    if (done) setTargets({ categories: '', merchants: '', campaigns: '' });
  };
  return (
    <form className="placement-form" onSubmit={(e) => void submit(e)}>
      <h3 className="section-title">Place this widget</h3>
      <div className="chip-list" role="group" aria-label="Sites">
        {sites.map((site) => (
          <label key={site.ref} className={`chip-toggle ${siteRefs.includes(site.ref) ? 'on' : ''}`}>
            <input type="checkbox" checked={siteRefs.includes(site.ref)} onChange={() => setSiteRefs((refs) => (refs.includes(site.ref) ? refs.filter((r) => r !== site.ref) : [...refs, site.ref]))} />
            {site.name}
          </label>
        ))}
      </div>
      <div className="form-grid cols-4">
        <label>
          Page type
          <select value={pageType} onChange={(e) => setPageType(e.target.value as PageType)}>
            {allowedPages.map((p) => (
              <option key={p} value={p}>
                {PAGE_LABELS[p]}
              </option>
            ))}
          </select>
        </label>
        <label>
          Region
          <select value={region} onChange={(e) => setRegion(e.target.value as Region)}>
            {(options.regionsByPage[pageType] ?? []).map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <label>
          From
          <input type="date" value={dates.startsAt} onChange={(e) => setDates((d) => ({ ...d, startsAt: e.target.value }))} />
        </label>
        <label>
          Until
          <input type="date" value={dates.endsAt} onChange={(e) => setDates((d) => ({ ...d, endsAt: e.target.value }))} />
        </label>
        <label>
          Only categories (slugs)
          <input value={targets.categories} onChange={(e) => setTargets((t) => ({ ...t, categories: e.target.value }))} placeholder="all" />
        </label>
        <label>
          Only stores (slugs)
          <input value={targets.merchants} onChange={(e) => setTargets((t) => ({ ...t, merchants: e.target.value }))} placeholder="all" />
        </label>
        <label>
          Only campaigns (slugs)
          <input value={targets.campaigns} onChange={(e) => setTargets((t) => ({ ...t, campaigns: e.target.value }))} placeholder="all" />
        </label>
        <label>
          Sold to brand (shows Sponsored)
          <input maxLength={80} value={sponsoredBy} onChange={(e) => setSponsoredBy(e.target.value)} placeholder="house content" />
        </label>
      </div>
      <button type="submit" className="btn btn-primary" disabled={siteRefs.length === 0}>
        Place
      </button>
    </form>
  );
}

export function WidgetEditor({ widgetRef }: { widgetRef: string | null }) {
  const changes = useChanges();
  const toast = useToast();
  const { navigate } = useRouter();
  const existing = useOp<Widget>('admin.widgets.get', { ref: widgetRef }, changes, widgetRef !== null);
  const options = useOp<WidgetOptions>('admin.widgets.options', {}, 0);
  const sitesOp = useOp<{ organizations: { brands: { sites: SiteRow[] }[] }[] }>('admin.sites.list', {}, 0);
  const sites = useMemo(() => (sitesOp.data?.organizations ?? []).flatMap((o) => o.brands.flatMap((b) => b.sites)), [sitesOp.data]);
  const [type, setType] = useState<WidgetType>('hero_carousel');
  const [name, setName] = useState('');
  const [config, setConfig] = useState<Config>(DEFAULT_CONFIG.hero_carousel);
  const [siteRef, setSiteRef] = useState('');
  useEffect(() => {
    if (existing.data) {
      setType(existing.data.type);
      setName(existing.data.name);
      setConfig({ ...DEFAULT_CONFIG[existing.data.type], ...existing.data.config });
    }
  }, [existing.data]);
  useEffect(() => {
    if (!siteRef && sites[0]) setSiteRef(sites[0].ref);
  }, [sites, siteRef]);
  useKeys({ escape: () => navigate('/widgets') });

  if (widgetRef && existing.error) return <ErrorBox error={existing.error} onRetry={existing.reload} />;
  if ((widgetRef && !existing.data) || !options.data) return <Loading />;
  const widget = existing.data;
  const canEdit = widgetRef === null || widget?.canEdit === true;

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (widgetRef === null) {
      const created = await runWrite<{ ref: string }>(toast, 'admin.widgets.create', { siteRef, type, name, config: cleaned(config) }, 'Widget created as a draft');
      if (created) navigate(`/widgets/${created.ref}`);
    } else {
      await runWrite(toast, 'admin.widgets.update', { ref: widgetRef, name, config: cleaned(config) }, 'Saved');
    }
  };
  const setStatus = (status: 'draft' | 'approved') =>
    void runWrite(toast, 'admin.widgets.update', { ref: widgetRef, status }, status === 'approved' ? 'Approved: shows on the sites where it is placed' : 'Back to draft: hidden on the sites');
  const remove = async () => {
    const done = await runWrite(toast, 'admin.widgets.delete', { ref: widgetRef }, 'Widget deleted');
    if (done) navigate('/widgets');
  };

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <p className="small">
            <Link to="/widgets">Widgets</Link>
          </p>
          <h1>{widget ? widget.name : 'New widget'}</h1>
          {widget ? (
            <p className="small muted">
              {WIDGET_LABELS[widget.type]} · <span className={`badge ${widget.status === 'approved' ? 'badge-ok' : ''}`}>{widget.status}</span>
            </p>
          ) : null}
        </div>
        {widget && canEdit ? (
          <div className="form-actions">
            {widget.status === 'draft' ? (
              <button type="button" className="btn btn-primary" onClick={() => setStatus('approved')}>
                Approve
              </button>
            ) : (
              <button type="button" className="btn" onClick={() => setStatus('draft')}>
                Back to draft
              </button>
            )}
            <button type="button" className="btn" onClick={() => void remove()}>
              Delete
            </button>
          </div>
        ) : null}
      </header>

      <form onSubmit={(e) => void save(e)}>
        <fieldset disabled={!canEdit}>
          <div className="form-grid">
            <label>
              Name (internal)
              <input required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nike Black Friday carousel" />
            </label>
            {widgetRef === null ? (
              <>
                <label>
                  Type
                  <select
                    value={type}
                    onChange={(e) => {
                      const next = e.target.value as WidgetType;
                      setType(next);
                      setConfig(DEFAULT_CONFIG[next]);
                    }}
                  >
                    {options.data.types.map((t) => (
                      <option key={t} value={t}>
                        {WIDGET_LABELS[t]}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Organization of site
                  <select required value={siteRef} onChange={(e) => setSiteRef(e.target.value)}>
                    {sites.map((s) => (
                      <option key={s.ref} value={s.ref}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : null}
          </div>
          <ConfigForm type={type} config={config} onChange={setConfig} />
          <div className="form-actions">
            <button type="submit" className="btn btn-primary">
              {widgetRef ? 'Save' : 'Create draft'}
            </button>
            <Link className="btn" to="/widgets">
              Back <Kbd>Esc</Kbd>
            </Link>
          </div>
        </fieldset>
      </form>

      {widget ? (
        <>
          <h2 className="section-title">Where it shows</h2>
          {widget.status === 'draft' ? <p className="small text-warn">Draft: placements are saved but nothing shows until the widget is approved.</p> : null}
          {widget.placements.length ? (
            <table className="table" aria-label="Placements">
              <thead>
                <tr>
                  <th scope="col">Site</th>
                  <th scope="col">Page and region</th>
                  <th scope="col">Only on</th>
                  <th scope="col">Dates</th>
                  <th scope="col">Sponsored by</th>
                  <th scope="col">State</th>
                  <th scope="col" />
                </tr>
              </thead>
              <tbody>
                {widget.placements.map((p) => (
                  <tr key={p.ref}>
                    <td>{p.siteName}</td>
                    <td>
                      {PAGE_LABELS[p.pageType]} · {p.region}
                    </td>
                    <td className="small">{[...(p.targets.categories ?? []), ...(p.targets.merchants ?? []), ...(p.targets.campaigns ?? [])].join(', ') || 'all'}</td>
                    <td className="small">{p.startsAt || p.endsAt ? `${p.startsAt ? formatDate(p.startsAt) : '…'} – ${p.endsAt ? formatDate(p.endsAt) : '…'}` : 'always'}</td>
                    <td>{p.sponsoredBy ?? <span className="muted">house</span>}</td>
                    <td>
                      <span className={`badge ${p.state === 'live' ? 'badge-ok' : ''}`}>{p.state}</span>
                    </td>
                    <td>
                      {p.canEdit ? (
                        <button type="button" className="btn btn-small" onClick={() => void runWrite(toast, 'admin.widgets.removePlacement', { ref: p.ref }, 'Placement removed')}>
                          Remove
                        </button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <Empty>Not placed yet.</Empty>
          )}
          {canEdit ? <PlacementForm widget={widget} options={options.data} sites={sites} /> : null}
        </>
      ) : null}
    </section>
  );
}
